import React, { useState } from "react";
import { FileText, Loader2, RefreshCw, Target, ThumbsUp, AlertCircle, Lightbulb, Radio, Swords } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const IMPACT = { "عالي": "bg-emerald-100 text-emerald-700", "متوسط": "bg-amber-100 text-amber-700", "منخفض": "bg-secondary text-muted-foreground" };

export const ExecSummary = ({ projectId, range }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (force) => {
    setBusy(true);
    try { const r = await api.post("/social/summary", { project_id: projectId || null, range: range || "month", force }); setData(r.data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="glass rounded-2xl p-5 border border-primary/30 bg-primary/5" data-testid="exec-summary-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /><h2 className="font-bold">الملخص التنفيذي وموقعك في السوق</h2></div>
        <Button size="sm" onClick={() => run(!!data)} disabled={busy} className="gap-1" data-testid="exec-summary-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {data ? "تحديث الملخص" : "إنشاء الملخص"}
        </Button>
      </div>
      {!data ? <p className="text-sm text-muted-foreground">يحلّل الذكاء الاصطناعي أداء حملتك عبر قنواتك ويحدد أفضل قناة للتركيز عليها مع أفضل الممارسات لزيادة الوصول، ويقترح أهم منافسيك في السعودية حسب نشاطك.</p> : (
        <div className="space-y-4" data-testid="exec-summary-body">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-20 h-20 rounded-2xl bg-primary text-white flex flex-col items-center justify-center shrink-0" data-testid="exec-score">
              <span className="text-2xl font-extrabold">{data.score ?? "—"}</span><span className="text-[10px] opacity-80">/100</span>
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="font-bold text-lg leading-snug" data-testid="exec-headline">{data.headline}</div>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed flex items-start gap-1"><Target className="w-4 h-4 mt-0.5 shrink-0 text-primary" /> {data.position}</p>
            </div>
          </div>
          {data.best_channel?.platform && (
            <div className="rounded-xl border border-accent/40 bg-accent/5 p-3" data-testid="exec-best-channel">
              <div className="text-xs font-bold text-accent flex items-center gap-1 mb-1"><Radio className="w-3.5 h-3.5" /> ركّز على: {data.best_channel.platform}</div>
              <p className="text-sm">{data.best_channel.why}</p>
              {(data.best_practices || []).length > 0 && <ul className="text-xs text-muted-foreground mt-2 space-y-1">{data.best_practices.map((s, i) => <li key={i}>✓ {s}</li>)}</ul>}
            </div>
          )}
          {(data.channels || []).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="exec-channels">
              {data.channels.map((c) => <span key={c.platform} className="text-[11px] border border-border rounded-full px-3 py-1 bg-background/60">{c.platform}: {c.measured}/{c.posts} مُقاس · متوسط التفاعل {c.avg_per_post}</span>)}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="border border-border rounded-xl p-3">
              <div className="text-xs font-bold text-emerald-600 flex items-center gap-1 mb-1"><ThumbsUp className="w-3.5 h-3.5" /> نقاط القوة</div>
              <ul className="text-sm space-y-1">{(data.strengths || []).map((s, i) => <li key={i}>• {s}</li>)}</ul>
            </div>
            <div className="border border-border rounded-xl p-3">
              <div className="text-xs font-bold text-rose-600 flex items-center gap-1 mb-1"><AlertCircle className="w-3.5 h-3.5" /> الفجوات</div>
              <ul className="text-sm space-y-1">{(data.gaps || []).map((s, i) => <li key={i}>• {s}</li>)}</ul>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold flex items-center gap-1 mb-2"><Lightbulb className="w-3.5 h-3.5 text-accent" /> توصيات النمو</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(data.recommendations || []).map((r, i) => (
                <div key={i} className="border border-border rounded-xl p-3 bg-background/60" data-testid={`recommendation-${i}`}>
                  <div className="flex items-center justify-between gap-2 mb-1"><span className="font-bold text-sm">{r.title}</span><span className={`text-[10px] px-2 py-0.5 rounded-full ${IMPACT[r.impact] || IMPACT["متوسط"]}`}>{r.impact}</span></div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{r.action}</p>
                </div>
              ))}
            </div>
          </div>
          {data.next_week_focus && <p className="text-sm bg-accent/10 border border-accent/30 rounded-lg px-3 py-2"><b>تركيز الأسبوع القادم:</b> {data.next_week_focus}</p>}
          {(data.suggested_competitors || []).length > 0 && (
            <div className="border border-border rounded-xl p-3" data-testid="exec-suggested-competitors">
              <div className="text-xs font-bold flex items-center gap-1 mb-1"><Swords className="w-3.5 h-3.5 text-accent" /> أهم منافسيك المحتملين في السعودية حسب نشاطك</div>
              <ul className="text-sm space-y-1">{data.suggested_competitors.map((c, i) => <li key={i}>• <b>{c.name}</b> — <span className="text-muted-foreground text-xs">{c.why}</span></li>)}</ul>
              <p className="text-[11px] text-muted-foreground mt-1">أضفهم مع هدفك في قسم المنافسين لتُوجَّه التوصيات لدعم توجهك.</p>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground" dir="ltr">{String(data.at || "").slice(0, 16).replace("T", " ")}</p>
        </div>
      )}
    </div>
  );
};
